import Copy from "@/components/copy";
import { DataTable } from "@/components/data-table";
import { DottedBorderBox } from "@/components/dotted-border";
import TopBar from "@/components/topbar";
import { Button } from "@/components/ui/button";
import { useGetRecentTransactions } from "@/hooks/use-queries";
import { useUser } from "@/zustand/store";
import {
  ArrowDownLeft,
  ArrowUpCircle,
  ArrowUpDown,
  ArrowUpRight,
  Eye,
  EyeOff,
  PlusCircle,
  Share2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { columns } from "../_misc/columns";
import { useModalStore } from "@/zustand/modalStore";
import { useSheetStore } from "@/zustand/sheetStore";
import TransactionList from "../_misc/TransactionList";
import { handleShare } from "@/lib/helper";
import { useState } from "react";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";

const Wallet = () => {
  const { user } = useUser((state) => state);
  const [showBalance, setShowBalance] = useState(true);
  const { openModal } = useModalStore();
  const { openSheet } = useSheetStore();
  const formatAmount = useFormatAmountWithCurrency();

  const { data: transactions, isLoading: transactionsIsLoading } =
    useGetRecentTransactions();

  return (
    <>
      <div>
        {/* header */}
        <TopBar title="My Wallet" />

        <div className="relative p-4">
          <img
            src="/images/bg-logo.png"
            alt=""
            className="fixed right-1/2 bottom-[calc(50%-100px)] z-0 translate-x-1/2 translate-y-1/2 lg:right-2/5"
          />

          <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-10">
            <div
              className="font-dm-sans relative box-border flex w-full flex-col justify-between gap-y-4 rounded-2xl border border-[#3A3B40] p-4 text-white lg:col-span-4 lg:h-full"
              style={{
                background:
                  "linear-gradient(104.01deg, #292934 2.88%, #454351 99.58%)",
              }}
            >
              <div className="flex justify-between">
                <img
                  src="/icons/gradient-wallet.svg"
                  className="size-8 lg:size-12"
                  alt="wallet icon"
                />
              </div>{" "}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium lg:text-sm">
                    Wallet Balance
                  </span>
                  <Button
                    variant={"ghost"}
                    onClick={() => setShowBalance(!showBalance)}
                    className="size-auto cursor-pointer !p-1 hover:bg-white/10"
                  >
                    {showBalance ? (
                      <EyeOff className="size-4 text-white/40" />
                    ) : (
                      <Eye className="size-4 text-white/40" />
                    )}
                  </Button>
                </div>
                <p className="text-xl font-semibold lg:text-2xl">
                  {showBalance ? formatAmount(user?.balance) : `****`}
                </p>
              </div>
              <div className="flex items-center gap-2.5 text-[#B2B2B2]">
                <span className="text-[10px]">Pending balance</span>
                <span className="text-sm">
                  {formatAmount(user?.pendingBalance)}
                </span>
              </div>
              <div className="hidden space-y-4 lg:block">
                <div className="text-bg-primary flex justify-between space-x-1.5 rounded-2xl *:grow">
                  <button
                    onClick={() => openModal("walletDeposit", { walletId: "" })}
                    className="lg:bg-primary-100 lg:hover:bg-primary-100/70 flex h-12 items-center justify-center gap-1 rounded-md px-2 py-2.5 transition-all duration-300 active:scale-95"
                  >
                    <PlusCircle className="size-6" strokeWidth={1.5} />
                    <p className="text-sm font-medium">Deposit</p>
                  </button>
                  <button
                    onClick={() => openSheet("withdraw", 2)}
                    className="lg:bg-primary-100 lg:hover:bg-primary-100/70 flex h-12 items-center justify-center gap-1 rounded-md px-2 py-2.5 transition-all duration-300 active:scale-95"
                  >
                    <ArrowUpCircle
                      className="size-6 rotate-45"
                      strokeWidth={1.5}
                    />

                    <p className="text-sm font-medium">Withdraw</p>
                  </button>
                  <button
                    onClick={() => openModal("transfer")}
                    className="lg:bg-primary-100 lg:hover:bg-primary-100/70 flex h-12 cursor-pointer items-center justify-center gap-1 rounded-md px-2 py-2.5 transition-all duration-300 active:scale-95"
                  >
                    <img
                      src="/icons/transfer.svg"
                      className="size-6"
                      alt="money send icon"
                    />
                    <p className="text-sm font-medium">Transfer</p>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2.5 *:grow lg:col-span-3">
              <div className="font-dm-sans relative hidden h-29 min-w-1/2 flex-col justify-center space-x-1.5 rounded-2xl bg-[linear-gradient(129.49deg,rgba(41,41,52,0.9)3.6%,rgba(72,72,93,0.9)100%)] p-4 lg:flex">
                <DottedBorderBox strokeColor={"#9191CB"} strokeWidth={0.5} />

                <p className="text-sm text-[#A8CAFF]">Pending balance</p>
                <p className="mt-2 text-2xl font-medium text-[#B2B2B2]">
                  {formatAmount(user?.pendingBalance)}
                </p>
              </div>
              <div className="font-dm-sans relative min-w-1/2 space-x-1.5 overflow-hidden rounded-2xl bg-[linear-gradient(129.49deg,rgba(41,41,52,0.9)3.6%,rgba(72,72,93,0.9)100%)] p-4 *:grow lg:h-29">
                <DottedBorderBox strokeColor={"#9191CB"} strokeWidth={0.5} />
                <div
                  className="absolute top-0 -right-2 px-2.5 py-2 text-[10px] font-light text-white uppercase"
                  style={{
                    background:
                      "linear-gradient(123.04deg, #9BBBFF 1.64%, #505E76 98.52%)",
                  }}
                >
                  Coming soon
                </div>
                <div className="opacity-30">
                  <img src="/icons/report.svg" alt="Report icon" />
                  <p className="mt-2 text-sm font-medium text-white">
                    Account Statement
                  </p>
                  <p className="mt-1 text-[10px] font-light text-white lg:text-xs">
                    View and track transactions from your wallet
                  </p>
                </div>
              </div>
            </div>

            <div className="font-dm-sans gradient-bg relative flex w-full justify-between space-x-1.5 rounded-2xl border border-[#8ECAEC]/30 px-3 py-2 *:grow lg:col-span-2 lg:hidden">
              <Button
                onClick={() => openModal("walletDeposit")}
                className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
              >
                <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                  <ArrowDownLeft className="size-4" strokeWidth={1.5} />
                </div>
                <p className="text-xs font-medium">Deposit</p>
              </Button>
              <Button
                onClick={() => openSheet("withdraw", 2)}
                className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
              >
                <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                  <ArrowUpRight className="size-4" strokeWidth={1.5} />
                </div>
                <p className="text-xs font-medium">Withdraw</p>
              </Button>
              <Button
                onClick={() => openModal("transfer")}
                className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
              >
                <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                  <ArrowUpDown className="size-4 rotate-45" strokeWidth={1.5} />
                </div>
                <p className="text-xs font-medium">Transfer</p>
              </Button>
            </div>

            {user?.canRefer && (
              <div
                className="col-span-3 hidden w-full grid-cols-2 flex-col justify-between gap-4 rounded-2xl px-2.5 py-4 text-white lg:flex"
                style={{
                  background:
                    "linear-gradient(123.04deg, #9BBBFF 1.64%, #505E76 98.52%)",
                }}
              >
                {/* total commission */}
                <div className="flex items-center justify-between">
                  <img
                    className="size-8 shrink-0"
                    src="/icons/gradient-users.svg"
                    alt="Total Commission"
                  />
                  <div className="text-right font-medium">
                    <p className="text-xs">Total Commission</p>
                    <p className="mt-2 text-xl leading-2">
                      {formatAmount(user?.totalCommisions)}
                    </p>
                  </div>
                </div>
                <div className="mt-6">
                  <p className="text-sm leading-4">
                    Refer friends to earn commission rewards.
                  </p>
                  <div className="mt-2 font-medium">
                    <p className="text-xs">Commission Rate</p>
                    <p className="mt-2 flex items-center gap-4 leading-2">
                      <span>
                        25 <span className="text-[10px]">%(Deposit)</span>
                      </span>{" "}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center gap-2.5 *:w-1/2">
                    <div
                      className={
                        "bg-primary-600 flex h-10 items-center justify-between rounded-md px-2.5"
                      }
                    >
                      <span className="w-5/6 overflow-hidden text-ellipsis whitespace-nowrap">
                        {user?.id}
                      </span>
                      <Copy
                        text={user?.id}
                        icon="/icons/copy2.svg"
                        side="left"
                      />
                    </div>
                    <Button
                      onClick={() => handleShare(`signup?ref=${user?.id}`)}
                      className={
                        "bg-primary-100 hover:bg-primary-100/80 text-primary-600 flex h-10 items-center justify-center gap-1 rounded-md hover:cursor-pointer"
                      }
                    >
                      <Share2 className="text-primary-600 size-4" />
                      <span>Share Your Link</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-bg-primary/90 mt-4 rounded-2xl p-4 text-white backdrop-blur-sm lg:row-start-auto">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">Recent Transactions</h2>
              <Link
                to="/dashboard/transactions"
                className="text-primary-100 text-xs hover:underline"
              >
                See All
              </Link>
            </div>

            <div className="mt-4">
              <div className="hidden lg:block">
                <DataTable columns={columns} data={transactions ?? []} />
              </div>
              <div className="lg:hidden">
                <TransactionList
                  isLoading={transactionsIsLoading}
                  transactions={transactions ?? []}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Wallet;
